// Taste signals (Feature 73) — Pinia store.
//
// Mirrors /api/ratings + /api/watched (invite-scoped, like My List). NOT
// persisted: the server is the source of truth and the rows are one cheap
// pair of GETs; a stale localStorage copy would fight the optimistic writes.
//
// Mutations are OPTIMISTIC: the local row flips immediately (hero buttons and
// rail exclusion react without a round-trip) and rolls back if the API call
// fails. Actions re-throw on rollback so a caller can surface feedback;
// fire-and-forget call sites should `.catch(() => {})`.

import { defineStore } from "pinia";
import * as tasteApi from "@/services/taste";
import { ApiError } from "@/services/apiClient";
import { useAuthStore } from "@/stores/auth";
import {
  tasteKey,
  buildExclusionSets,
  countPositiveRatings,
  hasEnoughRatings,
} from "@/helpers/taste-signals";

// Module-level in-flight latch (same pattern as services/seriesProgress.js
// hydratePromise) so concurrent load() calls from App.vue + a mounting shelf
// share one pair of GETs.
let inflight = null;

function normalizeKeyParts(mediaType, tmdbId) {
  const key = tasteKey(mediaType, tmdbId);
  if (!key) return null;
  return { key, mediaType, tmdbId: Number(tmdbId) };
}

function metaFields({ title, year } = {}) {
  const out = {};
  if (typeof title === "string" && title.trim()) out.title = title.trim();
  if (year != null && Number.isInteger(Number(year))) out.year = Number(year);
  return out;
}

export const useTasteStore = defineStore("taste", {
  state: () => ({
    // Server rows, camelCase: { tmdbId, mediaType, rating, title, year } and
    // { tmdbId, mediaType, state }.
    ratings: [],
    watched: [],
    loaded: false,
    loading: false,
  }),

  getters: {
    /**
     * Implicit ≥95%-played signals that carry a TMDB id. Currently EMPTY by
     * design — audited every cheap client-side source and none provides a
     * trustworthy "≥95% done + TMDB id" pair without new network fetches:
     *
     *   - playbackPosition rows CANNOT: savePosition() auto-DELETES a row the
     *     moment it crosses the 95% threshold (services/playbackPosition.js
     *     COMPLETION_THRESHOLD), so no finished row survives to read — and
     *     the surviving in-progress rows carry no tmdbId anyway (Put.io rows
     *     need an async thumbnailCache.getMatch to resolve one).
     *   - seriesProgress rows carry a tmdbId but NO completion ratio — they
     *     mean "engaged with season X", not "≥95% done", and treating them as
     *     watched would hide actively-watched shows from discovery rails.
     *
     * The explicit override path (Mark watched / the Continue Watching
     * dismiss, which writes state='watched') is the load-bearing feature.
     * buildExclusionSets fully supports implicit entries (incl. the explicit
     * 'unwatched' veto — covered by taste-signals tests), so a future signal
     * (e.g. the player's `ended` event announcing a TMDB-associated finish)
     * plugs in here without touching the exclusion logic.
     */
    implicitWatched: () => [],

    exclusionSets(state) {
      return buildExclusionSets({
        ratings: state.ratings,
        watched: state.watched,
        implicitWatched: this.implicitWatched,
      });
    },

    /** Effective watched — hide from GENERAL discovery rails (spec 73a). */
    watchedKeys() {
      return this.exclusionSets.watchedKeys;
    },

    /** Watched ∪ rated — hide from RECOMMENDATION rails (spec 73b). */
    seenForRecsKeys() {
      return this.exclusionSets.seenForRecsKeys;
    },

    isExcluded() {
      return (mediaType, id) => {
        const key = tasteKey(mediaType, id);
        return key ? this.watchedKeys.has(key) : false;
      };
    },

    isExcludedForRecs() {
      return (mediaType, id) => {
        const key = tasteKey(mediaType, id);
        return key ? this.seenForRecsKeys.has(key) : false;
      };
    },

    /** The explicit rating (-1 | 1 | 2) or null. */
    ratingFor(state) {
      return (mediaType, id) => {
        const key = tasteKey(mediaType, id);
        if (!key) return null;
        const row = state.ratings.find(
          (r) => tasteKey(r.mediaType, r.tmdbId) === key,
        );
        return row ? row.rating : null;
      };
    },

    /** The explicit override ('watched' | 'unwatched') or null (implicit-only
     *  state deliberately NOT reported here — the hero toggle reflects what
     *  the user explicitly set). */
    watchedStateFor(state) {
      return (mediaType, id) => {
        const key = tasteKey(mediaType, id);
        if (!key) return null;
        const row = state.watched.find(
          (r) => tasteKey(r.mediaType, r.tmdbId) === key,
        );
        return row ? row.state : null;
      };
    },

    positiveRatingCount: (state) => countPositiveRatings(state.ratings),

    /** ≥3 positive ratings — gates both recommendation rails. */
    hasEnoughRatings: (state) => hasEnoughRatings(state.ratings),
  },

  actions: {
    /**
     * Fetch ratings + watched in parallel, once. Re-entrant: concurrent and
     * repeated calls share/skip the round-trip, so App.vue's post-unlock warm
     * call and a shelf's own `await load()` are both safe. No-ops quietly
     * when the device isn't unlocked (a 401 would bounce the router to
     * /locked via apiClient — pointless from a background warm-up) and stays
     * un-`loaded` so a later post-unlock call retries. Never throws.
     */
    async load() {
      if (this.loaded) return;
      if (inflight) return inflight;
      try {
        if (!useAuthStore().isUnlocked) return;
      } catch {
        return; // Pinia/auth not ready — nothing to load yet.
      }
      inflight = (async () => {
        this.loading = true;
        try {
          const [ratings, watched] = await Promise.all([
            tasteApi.listRatings(),
            tasteApi.listWatched(),
          ]);
          this.ratings = Array.isArray(ratings) ? ratings : [];
          this.watched = Array.isArray(watched) ? watched : [];
          this.loaded = true;
        } catch {
          // Network failure or a just-expired session (apiClient already
          // handled any 401 redirect). Taste signals are an enhancement —
          // leave the store empty; the next load() call retries.
        } finally {
          this.loading = false;
          inflight = null;
        }
      })();
      return inflight;
    },

    /** Upsert a rating (-1 | 1 | 2). Optimistic; rolls back + re-throws on
     *  API failure. */
    async setRating(mediaType, tmdbId, rating, meta = {}) {
      const parts = normalizeKeyParts(mediaType, tmdbId);
      const value = Number(rating);
      if (!parts || (value !== -1 && value !== 1 && value !== 2)) return;
      const prev = this.ratings;
      const fields = metaFields(meta);
      this.ratings = [
        ...prev.filter((r) => tasteKey(r.mediaType, r.tmdbId) !== parts.key),
        {
          tmdbId: parts.tmdbId,
          mediaType,
          rating: value,
          title: fields.title ?? null,
          year: fields.year ?? null,
        },
      ];
      try {
        await tasteApi.setRating(mediaType, parts.tmdbId, value, fields);
      } catch (err) {
        this.ratings = prev;
        throw err;
      }
    },

    /** Remove a rating. Optimistic; a server 404 (row already gone) is the
     *  desired end state, so only real failures roll back. */
    async clearRating(mediaType, tmdbId) {
      const parts = normalizeKeyParts(mediaType, tmdbId);
      if (!parts) return;
      const prev = this.ratings;
      this.ratings = prev.filter(
        (r) => tasteKey(r.mediaType, r.tmdbId) !== parts.key,
      );
      try {
        await tasteApi.clearRating(mediaType, parts.tmdbId);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return;
        this.ratings = prev;
        throw err;
      }
    },

    /** Netflix-style tap-active-clears: tapping the currently-active rating
     *  clears it; any other value upserts. */
    async toggleRating(mediaType, tmdbId, rating, meta = {}) {
      if (this.ratingFor(mediaType, tmdbId) === Number(rating)) {
        return this.clearRating(mediaType, tmdbId);
      }
      return this.setRating(mediaType, tmdbId, rating, meta);
    },

    /** Upsert an explicit watched override ('watched' | 'unwatched').
     *  Optimistic; rolls back + re-throws on API failure. */
    async setWatched(mediaType, tmdbId, state, meta = {}) {
      const parts = normalizeKeyParts(mediaType, tmdbId);
      if (!parts || (state !== "watched" && state !== "unwatched")) return;
      const prev = this.watched;
      const fields = metaFields(meta);
      this.watched = [
        ...prev.filter((r) => tasteKey(r.mediaType, r.tmdbId) !== parts.key),
        { tmdbId: parts.tmdbId, mediaType, state },
      ];
      try {
        await tasteApi.setWatched(mediaType, parts.tmdbId, state, fields);
      } catch (err) {
        this.watched = prev;
        throw err;
      }
    },

    /** Remove the explicit override (back to the implicit signal). Optimistic;
     *  tolerates a server 404 like clearRating. */
    async clearWatched(mediaType, tmdbId) {
      const parts = normalizeKeyParts(mediaType, tmdbId);
      if (!parts) return;
      const prev = this.watched;
      this.watched = prev.filter(
        (r) => tasteKey(r.mediaType, r.tmdbId) !== parts.key,
      );
      try {
        await tasteApi.clearWatched(mediaType, parts.tmdbId);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return;
        this.watched = prev;
        throw err;
      }
    },

    /** Local clean-up (e.g. on logout) — matches liveTv.reset(). */
    reset() {
      this.ratings = [];
      this.watched = [];
      this.loaded = false;
      this.loading = false;
      inflight = null;
    },
  },
});
